import { z } from 'zod';
import { moderationStateSchema, topicStatusSchema, categoryKeySchema } from './enums';
/**
 * A topic, as read by any member.
 *
 * `authorDisplayName`/`authorDepartment` are frozen at write time (see
 * prisma/schema.prisma's comment on Topic) — a historical record, not a live
 * directory mirror, so a thread still renders correctly after someone
 * leaves. `categoryKey`/`categoryName`/`mainCategoryName` are the opposite —
 * resolved live through the category/main-category relation on every read,
 * so a rename shows up immediately everywhere. `categoryId`/`mainCategoryId`
 * are plain stored columns either way, used for filtering.
 *
 * `myVote` is resolved server-side against the signed-in principal — never
 * trust a client-supplied vote state.
 */
export const topicSchema = z.object({
    id: z.string(),
    categoryId: z.string(),
    categoryKey: categoryKeySchema,
    categoryName: z.string(),
    mainCategoryId: z.string(),
    mainCategoryName: z.string(),
    authorId: z.string(),
    authorDisplayName: z.string(),
    authorDepartment: z.string().nullable(),
    title: z.string(),
    body: z.string(),
    slug: z.string(),
    moderationState: moderationStateSchema,
    status: topicStatusSchema,
    voteScore: z.number().int(),
    commentCount: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    publishedAt: z.coerce.date().nullable(),
    /** Recently-updated/trending signal — see schema.prisma's comment on why this isn't just updatedAt. */
    lastActivityAt: z.coerce.date(),
    myVote: z.union([z.literal(1), z.literal(-1), z.null()]),
});
/**
 * `categoryId` addresses a specific (main-category-scoped) sub-category row
 * directly — the composer always has one in hand, having fetched "this main
 * category's categories" to build the picker, so there's no need for a
 * symbolic key that would otherwise be ambiguous across main categories.
 */
export const createTopicSchema = z.object({
    categoryId: z.string(),
    title: z.string().trim().min(1, 'Title is required').max(300),
    body: z.string().trim().min(1, 'Body is required').max(20_000),
});
/** Author self-edit. No `categoryId` — re-categorising a topic is not part of this. */
export const updateTopicSchema = z.object({
    title: z.string().trim().min(1, 'Title is required').max(300),
    body: z.string().trim().min(1, 'Body is required').max(20_000),
});
/**
 * `supportNotice` is not a property of the topic itself — it is a one-time
 * signal for the composer that just submitted this, telling the UI to show
 * the "this isn't a support channel" redirect. See ModerationService in the
 * API for the (deliberately separate, non-moderation) detection behind it.
 */
export const createTopicResponseSchema = z.object({
    topic: topicSchema,
    supportNotice: z.boolean(),
});
/**
 * recentlyUpdated orders by lastActivityAt directly. trending is a computed
 * "hot" score (recency and votes both count, older activity decays) — not
 * expressible as a plain column sort, see PrismaTopicRepository.findMany's
 * raw-SQL branch for it.
 */
export const topicSortSchema = z.enum(['recent', 'top', 'trending', 'recentlyUpdated']);
export const listTopicsQuerySchema = z.object({
    /** Required for a scoped browse (a specific main category); omitted for
     * the "All categories" combined view. */
    mainCategoryId: z.string().optional(),
    /** A specific sub-category's row id — only meaningful alongside `mainCategoryId`. */
    category: z.string().optional(),
    sort: topicSortSchema.default('recent'),
    page: z.coerce.number().int().min(1).default(1),
    /** Restricts the list to topics authored by the signed-in viewer — the
     * "My discussions only" filter. */
    mine: z.coerce.boolean().optional(),
    /** Keyword search across title/body — "find existing conversations before creating new ones". */
    q: z.string().trim().min(1).optional(),
});
export const topicListSchema = z.object({
    items: z.array(topicSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
//# sourceMappingURL=topics.js.map